import { supabase } from './supabase.js';
import { coerceUrl } from './url.js';

const SELECT_COLUMNS = [
  'id',
  'name',
  'type',
  'description',
  'website',
  'country',
  'city',
  'issn',
  'publisher',
  'frequency',
  'open_access',
  'status',
  'submitted_by',
  'created_at',
  'updated_at',
].join(', ');

export const ORG_TYPES = ['organization', 'institute', 'journal', 'society'];

export const EMPTY_ORG = {
  name: '',
  type: 'organization',
  description: '',
  website: '',
  country: '',
  city: '',
  issn: '',
  publisher: '',
  frequency: '',
  open_access: false,
};

export const EMPTY_ORG_FILTERS = {
  type: '',
  country: '',
};

export async function fetchPublishedOrgs() {
  const { data, error } = await supabase
    .from('organizations')
    .select(SELECT_COLUMNS)
    .eq('status', 'published')
    .order('name', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

export async function fetchOrg(id) {
  const { data, error } = await supabase
    .from('organizations')
    .select(SELECT_COLUMNS)
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return data;
}

function normalize(form) {
  const blank = (v) => (v == null || v === '' ? null : v);
  return {
    name: form.name?.trim(),
    type: form.type,
    description: blank(form.description),
    website: coerceUrl(form.website),
    country: blank(form.country?.trim?.()),
    city: blank(form.city?.trim?.()),
    issn: blank(form.issn?.trim?.()),
    publisher: blank(form.publisher?.trim?.()),
    frequency: blank(form.frequency?.trim?.()),
    // Only meaningful for journals; stored as NULL ("not known") otherwise.
    open_access: form.type === 'journal' ? Boolean(form.open_access) : null,
  };
}

export function validateOrg(form) {
  if (!form.name?.trim()) return 'organizations.form.errors.nameRequired';
  if (!ORG_TYPES.includes(form.type)) return 'organizations.form.errors.typeRequired';
  return null;
}

// announce_in_whats_new is only written on create; see migration 019.
export async function createOrg(form, { authorId, asAdmin }) {
  const row = {
    ...normalize(form),
    announce_in_whats_new: Boolean(form.announce_in_whats_new),
    status: asAdmin ? 'published' : 'pending',
    submitted_by: authorId,
  };
  const { data, error } = await supabase
    .from('organizations')
    .insert(row)
    .select(SELECT_COLUMNS)
    .single();
  if (error) throw error;
  return data;
}

export async function updateOrg(id, form) {
  const { data, error } = await supabase
    .from('organizations')
    .update(normalize(form))
    .eq('id', id)
    .select(SELECT_COLUMNS)
    .single();
  if (error) throw error;
  return data;
}

export async function deleteOrg(id) {
  const { error } = await supabase.from('organizations').delete().eq('id', id);
  if (error) throw error;
}

export function filterOrgs(orgs, { type, country, query }) {
  const q = (query ?? '').trim().toLowerCase();
  return orgs.filter((o) => {
    if (type && o.type !== type) return false;
    if (country && o.country !== country) return false;
    if (q) {
      const haystack = [o.name, o.publisher, o.description, o.city, o.country]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });
}

export function deriveOrgFacets(orgs) {
  const types = new Map();
  const countries = new Map();
  for (const o of orgs) {
    if (o.type) types.set(o.type, (types.get(o.type) ?? 0) + 1);
    if (o.country) countries.set(o.country, (countries.get(o.country) ?? 0) + 1);
  }
  return {
    types: ORG_TYPES.map((t) => ({ type: t, count: types.get(t) ?? 0 })),
    countries: [...countries.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name)),
  };
}
